import { useMemo, useEffect, useState, useRef, memo } from 'react';
import { LayoutDashboard, Mail, Bell, HelpCircle, Briefcase, Shield, Newspaper } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const navItems = [
  { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/business-portal', icon: Briefcase, label: 'Business Portal' },
  { to: '/admin/security-ops', icon: Shield, label: 'Security & Ops' },
  { to: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { to: '/admin/emails', icon: Mail, label: 'Emails' },
  { to: '/waitlist', icon: Mail, label: 'Waitlist' },
  { to: '/admin/help-support', icon: HelpCircle, label: 'Help & Support' },
  { to: '/admin/insights/create', icon: Newspaper, label: 'Orbmiv Insights' }
];

export const Sidebar = memo(({ isOpen, onClose }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sidebarRef = useRef(null);

    useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize nav items for perf
  const renderedNavItems = useMemo(
    () => navItems.map(({ to, icon: Icon, label }) => (
      <NavLink
        key={to}
        to={to}
        className={({ isActive }) =>
          `flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ease-in-out hover:shadow-soft ${
            isActive
              ? 'bg-primary-500 text-white shadow-lg'
              : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900 hover:animate-bounce-subtle'
          }`
        }
        onClick={() => onClose()} // Close on tap for mobile
      >
        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="whitespace-nowrap">{label}</span>
      </NavLink>
    )),
    [onClose]
  );

useEffect(() => {
  if (!isMobile) return;

  const handleClickOutside = (e) => {
    if (
      isOpen &&
      sidebarRef.current &&
      !sidebarRef.current.contains(e.target) &&
      !e.target.closest('button[aria-label="Toggle menu"]')
    ) {
      onClose();
    }
  };

  let originalOverflow = '';
  let originalPaddingRight = '';
  let scrollLockCleanup = () => {};

  if (isOpen) {
    document.addEventListener('click', handleClickOutside);

    // Client-only safe scroll lock with no layout shift
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      originalOverflow = document.body.style.overflow;
      originalPaddingRight = document.body.style.paddingRight;

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      scrollLockCleanup = () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }

  return () => {
    document.removeEventListener('click', handleClickOutside);
    scrollLockCleanup();
  };
}, [isOpen, onClose, isMobile]);

  // Sidebar classes - ensure it's always in DOM, use transforms/opacity for visibility
  const sidebarClasses = `
    fixed inset-y-0 left-0 z-50 w-64 bg-gray-50 border-r border-gray-200 p-4 space-y-2 transition-all duration-300 ease-in-out shadow-lg
    ${isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}
    md:relative md:translate-x-0 md:opacity-100 md:shadow-soft
  `;

  if (!isMobile && !isOpen) {
    // On desktop, only render if open? No, for persistent sidebar, always render but positioned relatively
    // Actually, for desktop, it's always visible, so no conditional render
  }

  return (
    <>
      {/* Mobile Overlay - only on mobile */}
      {isMobile && (
        <div
          className={`
            fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ease-in-out
            ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - always rendered */}
      <aside
        ref={sidebarRef}
        className={sidebarClasses}
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={!isOpen && isMobile}
      >
        <nav className="space-y-1 h-full flex flex-col justify-start">
          {renderedNavItems}
        </nav>
      </aside>
    </>
  );
});

Sidebar.displayName = 'Sidebar';